import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, ShieldCheck, HelpCircle } from "lucide-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useSeo } from "@/hooks/useSeo";

const INCLUDED = [
  "Unbegrenzte digitale Einwilligungen",
  "QR-Code-Patientenflow für Handy & Tablet",
  "Handschriftliche Unterschrift am Gerät",
  "Automatische, revisionssichere PDF-Erzeugung",
  "Datenschutzerklärung & Behandlungsvertrag versioniert",
  "AES-GCM-verschlüsselte Signaturen",
  "Hash-verkettete Audit-Logs & CSV-Export",
  "Mitarbeiterverwaltung mit Rollen & Rechten",
  "10-Jahres-Aufbewahrung mit Papierkorb",
  "Hosting in Deutschland (EU-Rechenzentrum)",
];

const FAQ = [
  { q: "Gilt der Preis pro Praxis?", a: "Ja. Der Preis von 69,99 € pro Monat gilt pro Praxis bzw. Standort – inklusive aller Mitarbeiterkonten dieser Praxis." },
  { q: "Was kostet die Einrichtung?", a: "Für die einmalige Bereitstellung und Einrichtung Ihrer Praxis berechnen wir 189,99 € netto. Danach zahlen Sie nur die monatliche Nutzungsgebühr." },
  { q: "Ist der Preis inklusive Mehrwertsteuer?", a: "Die monatlichen 69,99 € sind der Endpreis inkl. 19 % MwSt. Die einmalige Bereitstellung von 189,99 € ist ein Nettobetrag (zzgl. MwSt.)." },
  { q: "Kann ich monatlich kündigen?", a: "Ja, die monatliche Nutzung ist jederzeit zum Monatsende kündbar. Ihre Daten bleiben während der gesetzlichen Aufbewahrungsfrist sicher gespeichert." },
  { q: "Gibt es eine kostenlose Testphase?", a: "Ein kostenloser Testzeitraum ist auf Anfrage möglich. Fragen Sie einfach einen Testzugang an – wir schalten Ihre Praxis nach kurzer Prüfung frei." },
];

export default function Pricing() {
  useSeo(
    "Preise – LAIzyDocu | 69,99 € pro Monat und Praxis (inkl. MwSt.)",
    "Transparente Preise für LAIzyDocu: 69,99 € pro Monat und Praxis inkl. MwSt., monatlich kündbar. Einmalige Bereitstellung 189,99 € netto. Kostenloser Testzeitraum auf Anfrage.",
  );

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <SiteNav />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10 text-center">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-sm font-medium">
          <ShieldCheck className="w-4 h-4" /> Transparent & fair
        </span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-emerald-900 leading-[1.05] mt-6">
          Ein Preis. Alle Funktionen.
        </h1>
        <p className="text-lg text-stone-600 mt-6 leading-relaxed max-w-xl mx-auto">
          Keine Pakete, kein Kleingedrucktes. Eine Praxis, ein fairer Monatspreis – mit dem vollen
          Funktionsumfang von LAIzyDocu.
        </p>
      </section>

      {/* Pricing card */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-16">
        <div data-testid="pricing-card" className="bg-white rounded-[2rem] border border-stone-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] overflow-hidden md:grid md:grid-cols-5">
          {/* Price column */}
          <div className="bg-emerald-900 text-white p-8 md:p-10 md:col-span-2 flex flex-col justify-center">
            <div className="text-sm uppercase tracking-widest text-emerald-200/80">LAIzyDocu Engine</div>
            <div className="mt-4 flex items-end gap-1">
              <span data-testid="pricing-amount" className="font-display text-5xl md:text-6xl font-semibold">69,99 €</span>
            </div>
            <div className="text-emerald-100/90 mt-2">pro Monat & Praxis</div>
            <div className="text-emerald-200/70 text-sm mt-1">inkl. 19 % MwSt. · monatlich kündbar</div>

            <div data-testid="pricing-setup" className="mt-5 bg-emerald-800/40 border border-emerald-700/50 rounded-xl px-4 py-3">
              <div className="text-sm text-emerald-100/90">Einmalige Bereitstellung</div>
              <div className="font-display text-2xl font-semibold mt-0.5">189,99 € netto</div>
              <div className="text-emerald-200/70 text-xs mt-0.5">einmalig bei Einrichtung</div>
            </div>

            <div className="mt-5 inline-flex items-center gap-2 text-sm bg-emerald-800/50 border border-emerald-700/50 rounded-xl px-4 py-2.5 w-fit">
              <Check className="w-4 h-4" /> Kostenloser Testzeitraum auf Anfrage
            </div>
            <Link to="/registrieren-kaufen" data-testid="pricing-cta-buy" className="mt-8">
              <Button className="h-14 w-full rounded-xl bg-white text-emerald-900 hover:bg-stone-100 text-base font-medium">
                Jetzt buchen & loslegen <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/testzugang" data-testid="pricing-cta" className="mt-3 block">
              <Button variant="outline" className="h-12 w-full rounded-xl bg-transparent border-emerald-300/60 text-white hover:bg-emerald-800/50 text-sm">
                Stattdessen Testzugang anfragen
              </Button>
            </Link>
            <p className="text-xs text-emerald-200/70 mt-3 text-center">Sichere Zahlung über Mollie · jederzeit kündbar</p>
          </div>

          {/* Features column */}
          <div className="p-8 md:p-10 md:col-span-3">
            <h2 className="font-display text-xl font-semibold text-emerald-900 mb-5">Alles inklusive</h2>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3.5">
              {INCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-stone-700 text-sm leading-snug">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-20">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-emerald-900 mb-8">Häufige Fragen</h2>
        <div className="space-y-4">
          {FAQ.map((item) => (
            <div key={item.q} data-testid={`pricing-faq-${item.q.slice(0, 12)}`} className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-start gap-3">
                <HelpCircle className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-stone-900">{item.q}</h3>
                  <p className="text-stone-600 mt-1.5 leading-relaxed">{item.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
